import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export default function WellnessGrowthTrends({ individualTrends }) {
  const [view, setView] = useState('daily');

  const { chartData, overallTrend } = useMemo(() => {
    if (!individualTrends || individualTrends.length < 2) return { chartData: [], overallTrend: 0 };

    const sorted = [...individualTrends].sort((a, b) => new Date(a.date) - new Date(b.date));

    let data;
    if (view === 'daily') {
      // Show each daily entry as a data point
      data = sorted.map(d => ({
        label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        mood: +(d.mood || 0).toFixed(1),
        energy: +(d.energy || 0).toFixed(1),
        confidence: +(d.confidence || 0).toFixed(1),
        focus: +(d.focus || 0).toFixed(1),
        stress: +(d.stress || 0).toFixed(1),
        sleep: +(d.sleep_quality || 0).toFixed(1),
        overall: +(((d.mood || 0) + (d.energy || 0) + (d.confidence || 0) + (d.focus || 0)) / 4).toFixed(1),
      }));
    } else {
      // Weekly averages
      data = [];
      for (let i = 0; i < sorted.length; i += 7) {
        const chunk = sorted.slice(i, i + 7);
        if (chunk.length < 1) continue;
        const avg = (key) => +(chunk.reduce((s, d) => s + (d[key] || 0), 0) / chunk.length).toFixed(1);
        data.push({
          label: new Date(chunk[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          mood: avg('mood'), energy: avg('energy'), confidence: avg('confidence'),
          focus: avg('focus'), stress: avg('stress'), sleep: avg('sleep_quality'),
          overall: +((avg('mood') + avg('energy') + avg('confidence') + avg('focus')) / 4).toFixed(1),
        });
      }
    }

    let trend = 0;
    if (data.length >= 2) trend = data[data.length - 1].overall - data[0].overall;
    return { chartData: data, overallTrend: trend };
  }, [individualTrends, view]);

  if (chartData.length < 2) return null;

  const TrendIcon = overallTrend > 0.3 ? TrendingUp : overallTrend < -0.3 ? TrendingDown : Minus;
  const trendColor = overallTrend > 0.3 ? 'text-emerald-600' : overallTrend < -0.3 ? 'text-red-500' : 'text-gray-500';
  const trendLabel = overallTrend > 0.3 ? 'Improving' : overallTrend < -0.3 ? 'Declining' : 'Stable';

  return (
    <div data-testid="wellness-growth-trends" className="bg-white rounded-xl shadow-lg p-6 border border-emerald-100">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-bold text-gray-900">Growth Trends</h2>
        <div className="flex items-center gap-2">
          <div className="flex gap-1" data-testid="growth-view-toggle">
            {['daily', 'weekly'].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-2 py-1 rounded-lg text-[10px] font-semibold transition-all capitalize ${
                  view === v ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}>{v}</button>
            ))}
          </div>
          <div className={`flex items-center gap-1 ${trendColor}`}>
            <TrendIcon className="w-4 h-4" />
            <span className="text-xs font-semibold">{trendLabel}</span>
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-3">{view === 'daily' ? 'Daily metric entries' : 'Weekly averages'} over selected period</p>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} interval={view === 'daily' && chartData.length > 14 ? 2 : 0} />
          <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickCount={6} />
          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
            formatter={(val, name) => [`${val}/10`, name.charAt(0).toUpperCase() + name.slice(1)]} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: '10px' }} />
          <Line type="monotone" dataKey="overall" stroke="#059669" strokeWidth={2.5} dot={view === 'weekly' ? { r: 3, fill: '#059669' } : false} name="Overall" />
          <Line type="monotone" dataKey="mood" stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Mood" />
          <Line type="monotone" dataKey="energy" stroke="#22c55e" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Energy" />
          <Line type="monotone" dataKey="confidence" stroke="#a855f7" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Confidence" />
          <Line type="monotone" dataKey="stress" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Stress" />
          <Line type="monotone" dataKey="sleep" stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Sleep" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
